"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchNamespaceFeatures, type NamespaceFeatureState } from "@/lib/api-client";
import { queryKeys } from "@/lib/query/keys";
import { useActiveNamespaceId } from "@/hooks/useNamespaces";
import {
  DEFAULT_WORKSPACE_TABS,
  type WorkspaceTab,
} from "@/components/workspaces/workspace-tabs.config";

export function useNamespaceFeaturesQuery(namespaceId: string | null) {
  return useQuery({
    queryKey: queryKeys.namespaceFeatures(namespaceId ?? "none"),
    queryFn: () => fetchNamespaceFeatures(namespaceId!),
    enabled: Boolean(namespaceId),
    staleTime: 60_000,
  });
}

/**
 * Resolved on/off state of one product feature. Resolves against
 * `namespaceId` when given — surfaces editing a specific namespace (the
 * settings workspace) must gate on THAT namespace, which is not necessarily
 * the globally active one — and falls back to the active namespace for
 * ambient surfaces (nav, guards, assistant). `undefined` while the state is
 * still loading — callers should treat that as "not yet displayable", not as
 * enabled.
 */
export function useNamespaceFeatureEnabled(
  featureKey: string,
  namespaceId?: string | null,
): boolean | undefined {
  const activeNamespaceId = useActiveNamespaceId();
  const { data, isSuccess } = useNamespaceFeaturesQuery(namespaceId ?? activeNamespaceId);
  if (!isSuccess) return undefined;
  // A feature absent from the response is hidden (e.g. experimental tier off) —
  // absent means disabled, mirroring the backend's hard-off semantics.
  return data.some((f: NamespaceFeatureState) => f.key === featureKey && f.effective);
}

/**
 * The set of feature keys enabled for the active namespace, or `undefined`
 * while loading. Used by nav surfaces that gate several items in one render
 * (calling useNamespaceFeatureEnabled per item would break the rules of hooks).
 */
export function useEnabledNamespaceFeatureKeys(): ReadonlySet<string> | undefined {
  const namespaceId = useActiveNamespaceId();
  const { data, isSuccess } = useNamespaceFeaturesQuery(namespaceId);
  return useMemo(() => {
    if (!isSuccess) return undefined;
    return new Set(data.filter((f) => f.effective).map((f) => f.key));
  }, [data, isSuccess]);
}

/**
 * The workspace tabs the requested namespace may display (ambient when omitted).
 * Tabs without a `featureKey` always show; feature-gated tabs show only once the feature state
 * has loaded AND resolves enabled. Per the feature-flag contract
 * (docs/feature-flags.md), a disabled feature's workspace is not displayed —
 * so while loading, gated tabs stay hidden rather than flashing in and out.
 */
export function useAvailableWorkspaceTabs(namespaceId?: string | null): ReadonlyArray<WorkspaceTab> {
  const activeNamespaceId = useActiveNamespaceId();
  // Undefined preserves ambient previews; null explicitly waits for route resolution.
  const { data, isSuccess } = useNamespaceFeaturesQuery(namespaceId === undefined ? activeNamespaceId : namespaceId);

  return useMemo(() => {
    return DEFAULT_WORKSPACE_TABS.filter((tab) => {
      if (!tab.featureKey) return true;
      if (!isSuccess) return false;
      return data.some((f: NamespaceFeatureState) => f.key === tab.featureKey && f.effective);
    });
  }, [data, isSuccess]);
}
